import StoreModuleService from '../StoreModuleService';
import { MODULE_NAME } from './const';
import { useAppsWorkspaces } from '@/store/modules/workflows/getters';
import { getService, useDirectoryApp } from '@/store/modules/apps/getters';
import { getAllAccountConnections } from '@/store/modules/credentials/getters';
import CreateAndManageService from '@/views/AppLayout/ManageAndCreate/create-and-manage-service.js';

const buildAuthGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter, false);

// getters declarations
export const shouldFormatPropertyKey = buildAuthGetterObject('shouldFormatPropertyKey');
export const isLoggedIn = buildAuthGetterObject('isLoggedIn');
export const user = buildAuthGetterObject('user');
export const loadingLogin = buildAuthGetterObject('loadingLogin');
export const isUserRegistrationLoading = buildAuthGetterObject('isUserRegistrationLoading');
export const userInitials = buildAuthGetterObject('userInitials');
export const userFullName = buildAuthGetterObject('userFullName');
export const isAccountOwner = buildAuthGetterObject('isAccountOwner');
export const userTutorialsMetadata = buildAuthGetterObject('userTutorialsMetadata');
export const userOnBoardingMetadata = buildAuthGetterObject('userOnBoardingMetadata');
export const userProductHuntMetadata = buildAuthGetterObject('userProductHuntMetadata');
export const userMetadata = buildAuthGetterObject('userMetadata');
export const userFavoriteAppIds = buildAuthGetterObject('userFavoriteAppIds');
export const allUserConnectionsServiceDirectoriesIds = buildAuthGetterObject('allUserConnectionsServiceDirectoriesIds');
export const allUserAppsWorkspacesServiceDirectoriesIds = buildAuthGetterObject('allUserAppsWorkspacesServiceDirectoriesIds');
export const userFavoriteApps = buildAuthGetterObject('userFavoriteApps');
export const userRequestedAppIds = buildAuthGetterObject('userRequestedAppIds');
export const userRequestedApps = buildAuthGetterObject('userRequestedApps');
export const allCalculatedDirectoryApps = buildAuthGetterObject('allCalculatedDirectoryApps');
export const allCalculatedDirectoryAppsIds = buildAuthGetterObject('allCalculatedDirectoryAppsIds');
export const userFavoritePlyServicesIds = buildAuthGetterObject('userFavoritePlyServicesIds');
export const isLoadingUserData = buildAuthGetterObject('isLoadingUserData');

const getters = {
	[shouldFormatPropertyKey.getter]: (state, getters) => {
		const res = getters[userMetadata.getter].format_property_names;
		return res === undefined ? true : res;
	},
	[isLoadingUserData.getter]: (state) => state.isLoadingUserData,
	[userTutorialsMetadata.getter]: (state, getters) => getters[userMetadata.getter].tutorials,
	[userOnBoardingMetadata.getter]: (state, getters) =>
		getters[userMetadata.getter].onBoarding || { completedStepsIds: [], seenIntro: false, seenWelcomeToPlyDialog: false },
	[userProductHuntMetadata.getter]: (state, getters) => getters[userMetadata.getter].productHunt || { seen: false, hasAccount: false },
	[userMetadata.getter]: (state) => state.user.profile.metadata,
	[isLoggedIn.getter]: (state) => state.isLoggedIn,
	[user.getter]: (state) => state.user,
	[loadingLogin.getter]: (state) => state.loading,
	[isUserRegistrationLoading.getter]: (state) => state.isUserRegistrationLoading,
	[userInitials.getter]: (state) => {
		if (!state.user) {
			return '';
		}
		return state.user.first_name[0] + '' + state.user.last_name[0];
	},
	[userFullName.getter]: (state) => {
		if (!state.user) {
			return '';
		}
		return state.user.first_name + ' ' + state.user.last_name;
	},
	[isAccountOwner.getter]: (state) => state.user.id === state.user.profile.account?.owner.id,
	[userFavoriteAppIds.getter]: (state) => state.user.profile.apps_directory,
	[userFavoriteApps.getter]: (state, getters, rootState, rootGetters) =>
		getDirectoryAppsByIds(getters[userFavoriteAppIds.getter], rootGetters[useDirectoryApp.fullPath]),
	[userRequestedAppIds.getter]: (state) => state.user.profile.requested_app_directories,
	[userRequestedApps.getter]: (state, getters, rootState, rootGetters) =>
		getDirectoryAppsByIds(getters[userRequestedAppIds.getter], rootGetters[useDirectoryApp.fullPath]),
	[allCalculatedDirectoryApps.getter]: (state, getters, rootState, rootGetters) =>
		CreateAndManageService.getAllCalculatedDirectoryApps(
			getters[allUserAppsWorkspacesServiceDirectoriesIds.getter],
			getters[allUserConnectionsServiceDirectoriesIds.getter],
			getters[userFavoriteAppIds.getter],
			rootGetters[useDirectoryApp.fullPath]
		),
	[allUserConnectionsServiceDirectoriesIds.getter]: (state, getters, rootState, rootGetters) => {
		// extracts all of the users connections, and map them into an array of directory apps ids
		const allConnections = Object.values(rootGetters[getAllAccountConnections.fullPath]);
		return [...new Set(allConnections.map((connection) => rootGetters[getService.fullPath](connection?.service)?.service_directory))];
	},
	[allUserAppsWorkspacesServiceDirectoriesIds.getter]: (state, getters, rootState, rootGetters) =>
		// extracts all of the users apps workspaces, and map them into an array of directory apps ids
		rootGetters[useAppsWorkspaces.fullPath].map((app) => rootGetters[getService.fullPath](app?.id)?.service_directory),
	[allCalculatedDirectoryAppsIds.getter]: (state, getters) => getters[allCalculatedDirectoryApps.getter].map(({ id }) => id),
	[userFavoritePlyServicesIds.getter]: (state, getters) => {
		let allRelevantPlyServicesIds = [];
		getters[allCalculatedDirectoryApps.getter].forEach((app) => {
			allRelevantPlyServicesIds = [...allRelevantPlyServicesIds, ...app.related_services];
		});
		return [...new Set(allRelevantPlyServicesIds)]; // remove duplicates
	},
};

const getDirectoryAppsByIds = (ids, useDirectoryApp) => ids.map((id) => useDirectoryApp(id)).filter((app) => app);

export default getters;
